import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { judicialService, interventionService, caseService } from "@/services/api";
import { mapDbCaseToFrontend, mapDbInterventionToFrontend } from "@/services/mapping";
import Button from "@/components/base/Button";

interface ReportRecord {
  id: string;
  casoId: string;
  studentName: string;
  studentCurso: string;
  tipoReporte: string;
  contenidoBorrador: string;
  estado: string;
  fecha: string;
  casoTitulo?: string;
}

export default function OrientacionInformesPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [courtRequests, setCourtRequests] = useState<any[]>([]);
  const [reports, setReports] = useState<ReportRecord[]>([]);

  // Modal states
  const [generatingForRequest, setGeneratingForRequest] = useState<any | null>(null);
  const [viewingReport, setViewingReport] = useState<ReportRecord | null>(null);

  // Generator form states
  const [tipoInforme, setTipoInforme] = useState("integral");
  const [generando, setGenerando] = useState(false);
  const [generadoText, setGeneradoText] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [judicialData, intsData] = await Promise.all([
        judicialService.list().catch(() => []),
        interventionService.list().catch(() => [])
      ]);

      if (Array.isArray(judicialData)) {
        // Only active/pending judicial requirements
        const pending = judicialData.filter((cr: any) => cr.estado !== "respondido").map((cr: any) => ({
          id: cr.id,
          caseId: cr.caso_id,
          tribunal: cr.tribunal || "Tribunal de Familia",
          rit: cr.rit_ruc || "",
          numeroCausa: cr.rit_ruc || "",
          tipoInforme: cr.tipo_requerimiento || "Informe integral",
          fechaRecepcion: cr.fecha_recepcion ? new Date(cr.fecha_recepcion).toISOString().split("T")[0] : "",
          fechaLimite: cr.fecha_vencimiento ? new Date(cr.fecha_vencimiento).toISOString().split("T")[0] : "",
          estado: cr.estado || "pendiente",
          studentName: cr.estudiante_nombre || "Estudiante",
          studentCurso: cr.estudiante_curso || "Curso"
        }));
        setCourtRequests(pending);
      }

      if (Array.isArray(intsData)) {
        // Interventions of type 'informe_generado' represent generated reports
        const mappedReports: ReportRecord[] = intsData
          .filter((i: any) => i.tipo === "informe_generado")
          .map((i: any) => ({
            id: i.id,
            casoId: i.caso_id,
            studentName: i.estudiante_nombre || "Estudiante",
            studentCurso: i.estudiante_curso || "Curso",
            tipoReporte: `Informe de Orientación (${(i.acuerdos || "INTEGRAL").toUpperCase()})`,
            contenidoBorrador: i.descripcion || "",
            estado: "aprobado",
            fecha: i.fecha ? new Date(i.fecha).toISOString().split("T")[0] : "",
            casoTitulo: i.caso_titulo || "Caso"
          }));
        setReports(mappedReports);
      }
    } catch (err: any) {
      console.error("Error loading reports data:", err);
      setError("Error al cargar informes: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenGenerar = (cr: any) => {
    setGeneratingForRequest(cr);
    setTipoInforme("integral");
    setGenerando(false);
    setGeneradoText("");
    setIsEditing(false);
  };

  const handleSimulateGenerar = () => {
    setGenerando(true);
    setTimeout(() => {
      setGenerando(false);
      const studentName = generatingForRequest.studentName || "Estudiante";
      const curso = generatingForRequest.studentCurso || "Curso";
      const text = `INFORME DE EVALUACIÓN E INTERVENCIÓN DE ORIENTACIÓN
      
I. ANTECEDENTES GENERALES
Nombre Estudiante: ${studentName}
Curso: ${curso}
Tribunal: ${generatingForRequest.tribunal}
Causa: RIT ${generatingForRequest.numeroCausa}

II. ANTECEDENTES DE COMPORTAMIENTO Y CONVIVENCIA
El estudiante ha participado activamente en los talleres de mediación escolar convocados por el área de Convivencia y Orientación. Se observa disposición y compromiso en el cumplimiento de los acuerdos formativos adoptados en el plan de acompañamiento institucional.

III. DIAGNÓSTICO ESCOLAR Y OBSERVACIONES
De acuerdo a las entrevistas realizadas con el profesor jefe e inspectoría, se evidencia un avance conductual significativo en el aula. El apoderado ha asistido puntualmente a los llamados y compromisos escolares.

IV. SUGERENCIAS Y RECOMENDACIONES
- Mantener sesiones de acompañamiento individual cada quince días.
- Continuar con el refuerzo académico y adaptaciones metodológicas recomendadas.
- Monitorear periódicamente el clima de aula.

*Documento borrador generado automáticamente por el Asistente IA.*`;
      setGeneradoText(text);
    }, 1200);
  };

  const handleGuardarInforme = async () => {
    if (!generatingForRequest) return;
    
    try {
      setLoading(true);
      
      // 1. Create intervention of type 'informe_generado'
      await interventionService.create({
        caso_id: generatingForRequest.caseId,
        tipo: "informe_generado",
        descripcion: generadoText,
        fecha: new Date().toISOString().split("T")[0],
        acuerdos: tipoInforme,
        proximos_pasos: "",
        confidencial: false
      });

      // 2. Update court request status to respondido
      await judicialService.updateStatus(generatingForRequest.id, "respondido");

      setGeneratingForRequest(null);
      await loadData();
      alert("Informe IA generado y guardado exitosamente en Azure.");
    } catch (err: any) {
      console.error("Error saving generated report:", err);
      alert("Error al guardar informe: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDescargarWord = (report: ReportRecord) => {
    const element = document.createElement("a");
    const file = new Blob([report.contenidoBorrador], { type: 'application/msword;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = `Informe_${report.studentName.replace(/\s+/g, "_")}.doc`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDescargarPDF = (report: ReportRecord) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Informe - ${report.studentName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            h1 { text-align: center; color: #111; font-size: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            pre { white-space: pre-wrap; font-family: inherit; font-size: 14px; }
          </style>
        </head>
        <body>
          <h1>INFORME PARA TRIBUNAL - EZER CONVIVENCIA360</h1>
          <pre>${report.contenidoBorrador}</pre>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="p-4 md:p-6 space-y-5">
      <button
        onClick={() => navigate("/orientacion")}
        className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
      >
        <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-left-line text-sm" /></span>
        Volver al Dashboard
      </button>

      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Informes para Tribunal</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Gestión de oficios, solicitudes judiciales e informes generados</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {/* Requerimientos de Tribunal */}
      <div>
        <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3">Requerimientos de Tribunal Pendientes</h2>
        {loading ? (
          <div className="text-center py-12 bg-white border border-background-200 rounded-xl">
            <i className="ri-loader-4-line animate-spin text-lg text-primary-500 mr-2" /> Cargando requerimientos...
          </div>
        ) : courtRequests.length === 0 ? (
          <div className="text-center py-12 bg-white border border-background-200 rounded-xl text-foreground-400">
            <p className="text-sm">No hay requerimientos de tribunal registrados pendientes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {courtRequests.map((cr) => (
              <div key={cr.id} className="bg-white border border-background-200 rounded-xl p-5">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600">
                    <i className="ri-scales-3-line text-sm" />
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900">{cr.tribunal}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    cr.estado === "vencido" ? "bg-red-100 text-red-700"
                      : cr.estado === "en_proceso" ? "bg-yellow-100 text-yellow-700"
                      : "bg-background-100 text-foreground-600"
                  }`}>
                    {cr.estado === "vencido" ? "Vencido" : cr.estado === "en_proceso" ? "En proceso" : "Pendiente"}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 mb-3">
                  Estudiante: <strong>{cr.studentName}</strong> ({cr.studentCurso})
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-background-50 rounded-lg p-3">
                  <div>
                    <p className="text-xs text-foreground-400">Causa</p>
                    <p className="font-medium text-foreground-900 font-mono text-xs">{cr.numeroCausa}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Tipo de Informe</p>
                    <p className="font-medium text-foreground-900 capitalize">{cr.tipoInforme}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Recepción</p>
                    <p className="font-medium text-foreground-900">{cr.fechaRecepcion}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">Fecha Límite</p>
                    <p className={`font-medium ${cr.estado === "vencido" ? "text-red-600" : "text-foreground-900"}`}>{cr.fechaLimite}</p>
                  </div>
                </div>

                {cr.caseId ? (
                  <div className="mt-3 flex gap-2">
                    <button 
                      onClick={() => handleOpenGenerar(cr)}
                      className="px-3 py-1.5 text-xs bg-primary-500 text-white rounded-lg cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Generar Informe
                    </button>
                    <button
                      onClick={() => navigate(`/casos/${cr.caseId}`)}
                      className="px-3 py-1.5 text-xs border border-background-200 rounded-lg cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap"
                    >
                      Ver Caso
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-red-500">
                    *Para generar un informe debe asociar este requerimiento a un caso de convivencia u orientación desde la pestaña de Judiciales.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Informes Generados */}
      <div>
        <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-3">Informes Generados</h2>
        {loading ? (
          <div className="text-center py-12 bg-white border border-background-200 rounded-xl">
            <i className="ri-loader-4-line animate-spin text-lg text-primary-500 mr-2" /> Cargando informes...
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 bg-white border border-background-200 rounded-xl text-foreground-400">
            <p className="text-sm">No hay informes generados aún</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="bg-white border border-background-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-foreground-900">{r.tipoReporte}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                    Aprobado e Incorporado
                  </span>
                </div>
                <p className="text-xs text-foreground-500 mb-3">Estudiante: {r.studentName} · Caso: {r.casoTitulo} ({r.studentCurso})</p>
                <div className="bg-background-50 rounded-lg p-4 max-h-48 overflow-y-auto text-xs text-foreground-700 border border-background-100">
                  <pre className="font-sans whitespace-pre-wrap">{r.contenidoBorrador.substring(0, 300)}...</pre>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button 
                    onClick={() => handleDescargarWord(r)}
                    className="px-3 py-1.5 text-xs border border-background-200 rounded-lg cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap"
                  >
                    Descargar Word
                  </button>
                  <button 
                    onClick={() => handleDescargarPDF(r)}
                    className="px-3 py-1.5 text-xs border border-background-200 rounded-lg cursor-pointer hover:bg-background-50 transition-colors whitespace-nowrap"
                  >
                    Descargar PDF
                  </button>
                  <button 
                    onClick={() => setViewingReport(r)}
                    className="px-3 py-1.5 text-xs bg-primary-500 text-white rounded-lg cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Ver Completo
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MODAL: GENERAR INFORME ── */}
      {generatingForRequest && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setGeneratingForRequest(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-background-200">
              <h2 className="text-lg font-semibold text-foreground-950">Generar Informe con IA (Tribunal)</h2>
              <button onClick={() => setGeneratingForRequest(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!generadoText ? (
                <>
                  <div className="bg-background-50 rounded-lg p-3 text-sm text-foreground-600">
                    Generando informe oficial para causa <strong>{generatingForRequest.numeroCausa}</strong> - {generatingForRequest.studentName}.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Especialidad de Informe</label>
                    <select
                      value={tipoInforme}
                      onChange={(e) => setTipoInforme(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none cursor-pointer"
                    >
                      <option value="integral">Informe Integral de Convivencia</option>
                      <option value="orientacion">Informe Psicosocial y Orientación</option>
                      <option value="rendimiento">Historial Académico y Asistencia</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <Button 
                      onClick={handleSimulateGenerar} 
                      disabled={generando} 
                      className="bg-accent-500 hover:bg-accent-600 text-white cursor-pointer"
                    >
                      {generando ? "Generando borrador..." : "Iniciar Generación IA"}
                    </Button>
                    <Button variant="ghost" onClick={() => setGeneratingForRequest(null)} className="cursor-pointer">
                      Cancelar
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <i className="ri-check-line text-lg animate-bounce" />
                    Borrador redactado por la IA. Revisa y edita los campos si es necesario.
                  </div>
                  <div className="bg-background-50 rounded-lg p-4 max-h-80 overflow-y-auto border border-background-200">
                    {isEditing ? (
                      <textarea
                        value={generadoText}
                        onChange={(e) => setGeneradoText(e.target.value)}
                        rows={12}
                        className="w-full text-xs text-foreground-800 font-mono bg-white p-3 border border-primary-300 rounded-lg focus:outline-none"
                      />
                    ) : (
                      <pre className="text-xs text-foreground-700 font-sans whitespace-pre-wrap">{generadoText}</pre>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setIsEditing(!isEditing)}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-semibold cursor-pointer"
                    >
                      {isEditing ? "Guardar Cambios" : "Editar Borrador"}
                    </button>
                    <button 
                      onClick={handleGuardarInforme} 
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold cursor-pointer"
                    >
                      Aprobar e Incorporar
                    </button>
                    <button 
                      onClick={() => setGeneratingForRequest(null)} 
                      className="px-4 py-2 text-sm font-medium text-foreground-500 cursor-pointer"
                    >
                      Cancelar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODAL: VER COMPLETO ── */}
      {viewingReport && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setViewingReport(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-background-200">
              <h2 className="text-lg font-semibold text-foreground-950">{viewingReport.tipoReporte}</h2>
              <button onClick={() => setViewingReport(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-background-50 rounded-lg p-4 max-h-96 overflow-y-auto border border-background-200">
                <pre className="text-xs text-foreground-700 font-sans whitespace-pre-wrap">{viewingReport.contenidoBorrador}</pre>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-background-200">
                <button 
                  onClick={() => handleDescargarWord(viewingReport)} 
                  className="px-4 py-2 border border-background-200 rounded-lg text-sm cursor-pointer hover:bg-background-50 transition-colors"
                >
                  Descargar Word
                </button>
                <button 
                  onClick={() => handleDescargarPDF(viewingReport)} 
                  className="px-4 py-2 border border-background-200 rounded-lg text-sm cursor-pointer hover:bg-background-50 transition-colors"
                >
                  Descargar PDF
                </button>
                <Button onClick={() => setViewingReport(null)} className="bg-primary-500 text-white cursor-pointer ml-auto">
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}